import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectWorkingProjectUuid } from '@/store/slices/project/selectors';
import {
  selectErrorLoadingPublishedScheduledEmails,
  selectLoadingPublishedScheduledEmails,
  selectPublishedScheduledEmails,
  selectPublishedScheduledEmailsEventUuid,
} from '@/store/slices/scheduled-emails/selectors';
import { loadEventPublishedScheduledEmails } from '@/store/slices/scheduled-emails';

/**
 * Loads the published version of the event's scheduled emails (campaigns) - what will actually send, ignoring
 * unpublished draft changes. Reloads when the event changes or a create/publish/delete clears them. Doesn't retry
 * after a failure - a failed load leaves them unset, which would otherwise re-trigger it forever.
 */
const useLoadEventPublishedScheduledEmails = () => {
  const dispatch = useAppDispatch();
  const projectUuid = useAppSelector(selectWorkingProjectUuid);
  const publishedEmails = useAppSelector(selectPublishedScheduledEmails);
  const publishedEmailsEventUuid = useAppSelector(selectPublishedScheduledEmailsEventUuid);
  const loading = useAppSelector(selectLoadingPublishedScheduledEmails);
  const error = useAppSelector(selectErrorLoadingPublishedScheduledEmails);

  const isOtherEvent = publishedEmailsEventUuid !== projectUuid;

  useEffect(() => {
    if (!projectUuid) return;
    if (isOtherEvent || (!loading && !error && !publishedEmails)) {
      dispatch(loadEventPublishedScheduledEmails(projectUuid));
    }
  }, [dispatch, projectUuid, isOtherEvent, loading, error, publishedEmails]);

  return { publishedEmails: isOtherEvent ? null : publishedEmails, loadingPublishedEmails: loading };
};

export default useLoadEventPublishedScheduledEmails;
